"use client"
import { useTranslations } from "next-intl"
import { useLocale } from "next-intl"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import type { StepProps, PhotoItem } from "@/types/listing"
import Image from "next/image"
import { Check, ChevronRight, Pencil } from "lucide-react"
import { motion } from "framer-motion"
import { createClient } from "@/utils/supabase/client"
import { v4 as uuidv4 } from "uuid"
import { getNormalizedImageFile, getSafeImageExtension } from "@/lib/image-upload"
import { formatPrice } from "@/lib/utils"
import { storageBucket, StoragePath } from "@/lib/storage"
import { computeAuctionEndAt, formatAuctionEndCopy } from "@/lib/auction-timing"
import { Switch } from "@/components/ui/switch"
import {
  creationFlowBlockClass,
  creationFlowSectionLabelClass,
  creationFlowPrimaryCtaTallClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { asRawImageRef, rawImageRefValue, resolveStorageUrl, type RawImageRef } from "@/lib/storage/public-url"
import { useToast } from "@/hooks/use-toast"
import {
  createListing,
  CreateListingError,
  type CreateListingIntent,
} from "@/lib/listings/listing-create"
import { isPersistedImageRef } from "@/lib/storage/url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

const supabase = createClient()
// Canonical predicate — a bucket-relative storage path counts as persisted
// too, otherwise every freshly-uploaded image is filtered out once writers
// stop calling getPublicUrl(). @see lib/storage/url.ts
const isHttpUrl = isPersistedImageRef

export function ReviewStep({ data, updateDataAction, onSubmit, hideButton, onValidationChange, onRegisterSave, onGoToStepAction, compact }: StepProps) {
  const locale = useLocale()
  const tLC = useTranslations("listingCreation");
  const [isCreating, setIsCreating] = useState(false)
  const { toast } = useToast()
  const [categoryNames, setCategoryNames] = useState<{brand?: string, theme?: string, productType?: string}>({})

  // ReviewStep is always valid (all validation is done in previous steps)
  useEffect(() => {
    onValidationChange?.(true)
  }, [onValidationChange])

  // Fetch category names
  useEffect(() => {
    async function fetchCategoryNames() {
      const ids = [data.brand_id, data.theme_id, data.product_type_id].filter(Boolean)
      if (ids.length === 0) return

      const { data: taxonomyData, error } = await supabase
        .from('taxonomy_items')
        .select('id, name, type')
        .in('id', ids)

      if (!error && taxonomyData) {
        const names: any = {}
        taxonomyData.forEach(item => {
          const display = (n: string, custom?: string | null) => (n?.toLowerCase() === "other" && custom ? custom : n)
          if (item.id === data.brand_id) names.brand = display(item.name, data.brand_custom_text)
          if (item.id === data.theme_id) names.theme = display(item.name, data.theme_custom_text)
          if (item.id === data.product_type_id) names.productType = display(item.name, data.product_type_custom_text)
        })
        setCategoryNames(names)
      }
    }
    fetchCategoryNames()
  }, [data.brand_id, data.theme_id, data.product_type_id, data.brand_custom_text, data.theme_custom_text, data.product_type_custom_text])

  const conditionLabel = (() => {
    if (!data.condition) return tLC("reviewNotSpecified")
    if (data.condition === "new") return tLC("conditions.new.label")
    if (data.condition === "pre-owned") return tLC("conditions.preOwned.label")
    if (data.condition === "custom") return tLC("conditions.custom.label")
    return data.condition
  })()

  const gradeLabel = (() => {
    const draft = data as { grade?: string; gradeLabel?: string }
    if (!draft.grade) return tLC("reviewNotSpecified")
    if (draft.gradeLabel) return draft.gradeLabel
    const numericGrade = draft.grade.replace(/\D/g, "") || draft.grade
    if (/^(10|[1-9])$/.test(numericGrade)) {
      return tLC(`grades.${numericGrade}.label`)
    }
    return draft.grade
  })()

  // Default shipping options (if any)
  const defaultShippingMap: Record<string, { label: string; price: string }> = {
    standard: { label: "Standard Shipping", price: "5.00" },
    express: { label: "Express Shipping", price: "10.00" },
    // … add other defaults if needed
  }

  // Custom shipping options from data
  const customShippingOptions = data.shipping?.customOptions || []

  // Build shipping configuration to store in listings (as JSON)
  const buildShippingConfig = () => {
    const selectedMethodIds = data.shipping?.methods || []
    const configArray: any[] = []

    // Add custom shipping methods
    customShippingOptions.forEach((option: any) => {
      if (selectedMethodIds.includes(option.id)) {
        configArray.push({
          shipping_method_id: option.id,
          label: option.label || option.name,
          price: option.price,
          allow_combined: option.allowCombined || false,
          combined_fee: option.combinedFee || "0",
        })
      }
    })

    // Add default shipping methods
    selectedMethodIds.forEach((methodId: string) => {
      if (defaultShippingMap[methodId]) {
        const def = defaultShippingMap[methodId]
        if (!configArray.find((c) => c.shipping_method_id === methodId)) {
          configArray.push({
            shipping_method_id: methodId,
            label: def.label,
            price: def.price,
            allow_combined: false,
            combined_fee: "0",
          })
        }
      }
    })

    return configArray
  }

  // Extract photo URLs from PhotoItem array
  // Stays RAW: the result feeds `finalPhotos` -> `image_urls` (handoff §9).
  const extractPhotoUrls = (photos: PhotoItem[]): RawImageRef[] => {
  if (!photos || !Array.isArray(photos)) return []
  return photos
    .map((photo) => photo?.preview)
    .filter((url): url is RawImageRef => Boolean(url) && isHttpUrl(url))
}

  const handleCreateListing = async () => {
    setIsCreating(true)

    // 🎯 CRITICAL FIX: Maintain exact order of photos as received from previous steps
    const sourcePhotos =
      Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos
    const photosCopy = [...sourcePhotos]
    const previewUrls = extractPhotoUrls(photosCopy)
    const uploadedUrls: RawImageRef[] = []
    const expectedPhotoCount = photosCopy.filter((photo) => Boolean(photo?.preview)).length
    const attemptPhotoUpload = async (sourceFile: File, index: number): Promise<RawImageRef | null> => {
      let fileToUpload = sourceFile
      try {
        const normalized = await getNormalizedImageFile(sourceFile)
        fileToUpload = normalized.file
      } catch (error) {
        console.error(`❌ Error normalizing image file ${index}:`, error)
      }
      const extension = getSafeImageExtension(fileToUpload)
      const filePath = StoragePath.listing(data.title, index, extension)
      try {
        const { error: uploadError } = await storageBucket(supabase).upload(filePath, fileToUpload)
        if (uploadError) {
          console.error(`❌ Error uploading photo ${index}:`, uploadError)
          return null
        }
        // Bucket-relative path -> listings.image_urls.
        return asRawImageRef(filePath)
      } catch (error) {
        console.error(`❌ Error in upload process for photo ${index}:`, error)
        return null
      }
    }
    const recoverBlobPreview = async (previewUrl: RawImageRef, index: number): Promise<RawImageRef | null> => {
      try {
        // `blob:` passes through the resolver untouched; going through it keeps
        // the sink honest if this branch ever widens to a storage path.
        const response = await fetch(resolveStorageUrl(previewUrl))
        if (!response.ok) {
          console.warn(`⚠️ Failed to fetch blob preview at index ${index}`, response.status)
          return null
        }
        const blobData = await response.blob()
        const recoveredFile = new File(
          [blobData],
          `blob-preview-${Date.now()}-${index}.jpg`,
          {
            type: blobData.type || "image/jpeg",
            lastModified: Date.now(),
          }
        )
        return attemptPhotoUpload(recoveredFile, index)
      } catch (error) {
        console.error(`⚠️ Error recovering blob preview at index ${index}:`, error)
        return null
      }
    }

    // 🎯 CRITICAL FIX: Process photos in EXACT sequential order (no parallel processing)
    for (let i = 0; i < photosCopy.length; i++) {
      const photo = photosCopy[i]
      
      if (photo?.file instanceof File) {
        const file = photo.file
        const MAX_FILE_SIZE = 10 * 1024 * 1024
        if (file.size > MAX_FILE_SIZE) {
          console.error(`❌ File too large: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`)
          continue
        }
        const uploadedUrl = await attemptPhotoUpload(file, i)
        if (uploadedUrl) {
          uploadedUrls.push(uploadedUrl)
        } else if (photo.preview && isHttpUrl(photo.preview)) {
          uploadedUrls.push(photo.preview)
        }
      } else if (photo?.preview) {
        if (isHttpUrl(photo.preview)) {
          uploadedUrls.push(photo.preview)
        } else if (rawImageRefValue(photo.preview).startsWith("blob:")) {
          const recoveredUrl = await recoverBlobPreview(photo.preview, i)
          if (recoveredUrl) {
            uploadedUrls.push(recoveredUrl)
          } else {
            console.warn(`⚠️ ReviewStep: Failed to recover blob preview ${i}:`, photo.photoId)
          }
        }
      } else {
        console.warn(`⚠️ ReviewStep: Skipping invalid photo ${i}:`, photo)
      }
    }

    const sanitizedUploads = uploadedUrls.filter((url) => isHttpUrl(url))
    let finalPhotos: RawImageRef[] = []
    if (sanitizedUploads.length === expectedPhotoCount && expectedPhotoCount > 0) {
      finalPhotos = sanitizedUploads
    } else if (sanitizedUploads.length === 0 && previewUrls.length === expectedPhotoCount) {
      finalPhotos = previewUrls
    } else if (expectedPhotoCount === 0) {
      finalPhotos = []
    } else {
      toast({ title: "Error", description: "Some photos could not be processed. Please try again.", variant: "destructive" })
      setIsCreating(false)
      return
    }
    const shippingConfig = buildShippingConfig()

    try {
      const listingId = uuidv4()
      // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
      const isChatPrivate = (data as any).visibility === "chat_private"
      const chatPrivateSaved = Boolean((data as any).chat_private_saved)
      const isAuction = (data as { listing_format?: string }).listing_format === "auction"
      const isOffersOnly = (data as { listing_format?: string }).listing_format === "offers_only"
      const quantity = isAuction ? 1 : (data.quantity ?? 0)
      const shippingMode = data.shippingMode ?? "legacy"
      const isLiveRate = shippingMode === "live_rate"
      const parcel = data.parcel
      const listingData: Record<string, unknown> = {
        id: listingId,
        title: data.title,
        description: data.description || "",
        price: isAuction || isOffersOnly ? null : Number(data.price ?? 0),
        quantity,
        shipping_config: shippingConfig,
        // listings.location dropped — ADR_LISTING_SHIP_FROM_ADDRESS Phase 3.
        image_urls: finalPhotos,
        condition: data.condition || "",
        grade: data.grade || "",
        brand_id: data.brand_id || null,
        theme_id: data.theme_id || null,
        product_type_id: data.product_type_id || null,
        theme_custom_text: data.theme_custom_text ?? null,
        brand_custom_text: data.brand_custom_text ?? null,
        product_type_custom_text: data.product_type_custom_text ?? null,
        scheduled_publish_at: isChatPrivate ? null : (data.scheduled_publish_at || null),
        is_dev_listing: isChatPrivate ? false : (data.is_dev_listing ?? false),
        visibility: isChatPrivate ? "chat_private" : "public",
        chat_private_saved: isChatPrivate ? chatPrivateSaved : false,
        shipping_mode: shippingMode,
        carrier_provider: isLiveRate ? "shippo" : null,
        // Canonical-only (slice C2): the form emits grams + mm; we forward as-is.
        parcel_weight_grams: isLiveRate ? parcel?.weight ?? null : null,
        parcel_length_mm: isLiveRate ? parcel?.length ?? null : null,
        parcel_width_mm: isLiveRate ? parcel?.width ?? null : null,
        parcel_height_mm: isLiveRate ? parcel?.height ?? null : null,
      }
      if (isAuction) {
        listingData.listing_format = "auction"
        listingData.auction_start_price = Number((data as { auction_start_price?: number }).auction_start_price ?? 0)
        listingData.auction_reserve_price = (data as { auction_reserve_price?: number | null }).auction_reserve_price ?? null
      } else {
        // Phase 3 (2026-06-29): `accepts_offers` collapsed into
        // `listing_format`. DetailsStep emits the final format
        // (`fixed_price_with_offers` when the toggle is on) — pass it through.
        const fmt = (data as { listing_format?: string }).listing_format
        if (fmt === "offers_only" || fmt === "fixed_price_with_offers" || fmt === "fixed_price") {
          listingData.listing_format = fmt
          listingData.auto_decline_below = (data as { auto_decline_below?: number | null }).auto_decline_below ?? null
        }
      }

      if ((data as { listing_type?: string }).listing_type === "preorder") {
        listingData.listing_type = "preorder"
        listingData.preorder_deadline = (data as { preorder_deadline?: string | null }).preorder_deadline ?? null
        listingData.estimated_delivery = (data as { estimated_delivery?: string | null }).estimated_delivery ?? null
        listingData.min_orders = (data as { min_orders?: number | null }).min_orders ?? null
        listingData.max_orders = (data as { max_orders?: number | null }).max_orders ?? null
      }

      let result: { listingId: string }
      try {
        result = await createListing(listingData as CreateListingIntent)
      } catch (apiErr) {
        if (apiErr instanceof CreateListingError) {
          const message = apiErr.serverError ?? "Could not create listing. Please try again."
          toast({ title: "Error", description: message, variant: "destructive" })
          return
        }
        throw apiErr
      }

      const resolvedListingId = result.listingId || listingId
      const finalPhotoItems = finalPhotos.map((url, index) => ({
        file: null,
        preview: url,
        photoId: `uploaded-${index}-${Date.now()}`,
      }))
      updateDataAction({ photos: finalPhotoItems, editedPhotos: finalPhotoItems, photoEditorState: undefined, id: resolvedListingId })
      if (onSubmit) onSubmit()
    } catch (err: any) {
      console.error("Error in create listing process:", err)
      toast({ title: "Error", description: "An unexpected error occurred.", variant: "destructive" })
    } finally {
      setIsCreating(false)
    }
  }

  // Register create handler so the container's footer button can trigger it
  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(() => {
        if (!isCreating) {
          return handleCreateListing()
        }
      })
      return () => onRegisterSave(null)
    }
  }, [onRegisterSave, isCreating])

  const editBtn = (step: number) =>
    onGoToStepAction ? (
      <button
        type="button"
        onClick={() => onGoToStepAction(step)}
        className="ml-auto flex items-center gap-1 text-xs font-medium rounded-lg px-2 py-1 transition-colors text-muted-foreground hover:text-foreground hover:bg-foreground/10"
      >
        <Pencil className="w-3 h-3" />
        {tLC("edit")}
      </button>
    ) : null

  const cardRadius = "rounded-3xl"
  const cardBase = creationFlowBlockClass
  const cardPadding = compact ? "p-3" : "p-4"
  const sectionLabel = creationFlowSectionLabelClass
  const cellBase = "rounded-xl bg-muted/80 p-3"
  const mutedClass = "text-muted-foreground/70"
  const textClass = "text-foreground"

  return (
    <div className={compact ? "space-y-4 px-1" : "space-y-6 px-2"}>
      <motion.div className="space-y-5" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={cn(cardRadius, cardBase, cardPadding)}
        >
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>
              {tLC("reviewPhotos")} ({(Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos).length})
            </p>
            {editBtn(1)}
          </div>
          <div className="flex overflow-x-auto gap-2 pb-1 scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]">
            {(Array.isArray(data.editedPhotos) && data.editedPhotos.length > 0 ? data.editedPhotos : data.photos).map((photo: PhotoItem, index: number) => {
              const src = photo?.preview || ""
              if (!src) return null
              return (
                <div
                  key={index}
                  className="relative w-20 shrink-0 overflow-hidden rounded-xl aspect-3/4 bg-muted"
                >
                  <Image
                    // `photo.preview` is TWO different things over this screen's
                    // life: a local `blob:` before the create, and the uploaded
                    // bucket-relative path after `updateDataAction` rewrites it.
                    // `imagePresetUrl` handles both — it resolves the path (so
                    // that frame cannot crash next/image) and passes `blob:`
                    // through untouched (nothing to resize; the bytes are
                    // already in memory).
                    //
                    // `unoptimized` STAYS, and its reason has changed. It was
                    // never doing anything for the `blob:` frame — next/image
                    // force-sets `unoptimized` itself for `data:`/`blob:` srcs
                    // (`next/dist/shared/lib/get-img-props.js`, "if (!src ||
                    // src.startsWith('blob:'))"). It is now load-bearing for the
                    // OTHER frame: the stored path arrives already CDN-sized, so
                    // handing it to the optimizer would buy a server hop and a
                    // billed transformation for the same pixels (handoff §2.4).
                    //
                    // CARD_SM, not a square SQUARE_*: the tile is 80x107
                    // (`aspect-3/4`) and already crops via `object-cover`, so a
                    // square CDN crop would crop a second time and quietly tighten
                    // the framing on the one screen whose whole job is "check your
                    // photos before publishing".
                    src={imagePresetUrl(src, CARD_SM) || "/placeholder.svg"}
                    alt={`Product photo ${index + 1}`}
                    fill
                    className="object-cover"
                    unoptimized
                  />
                </div>
              )
            })}
          </div>
        </motion.div>

        {(categoryNames.brand || categoryNames.theme || categoryNames.productType) && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className={cn(cardRadius, cardBase, cardPadding)}
          >
            <div className="flex items-center mb-3">
              <p className={cn(sectionLabel)}>{tLC("reviewCategory")}</p>
              {editBtn(3)}
            </div>
            <div className="grid grid-cols-1 gap-2">
              {categoryNames.brand && (
                <div className={cellBase}>
                  <p className={cn("text-xs", mutedClass)}>{tLC("reviewBrand")}</p>
                  <p className={cn("text-sm font-medium", textClass)}>{categoryNames.brand}</p>
                </div>
              )}
              {categoryNames.theme && (
                <div className={cellBase}>
                  <p className={cn("text-xs", mutedClass)}>{tLC("reviewTheme")}</p>
                  <p className={cn("text-sm font-medium", textClass)}>{categoryNames.theme}</p>
                </div>
              )}
              {categoryNames.productType && (
                <div className={cellBase}>
                  <p className={cn("text-xs", mutedClass)}>{tLC("reviewProductType")}</p>
                  <p className={cn("text-sm font-medium", textClass)}>{categoryNames.productType}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={cn(cardRadius, cardBase, cardPadding)}
        >
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{tLC("reviewDetails")}</p>
            {editBtn(4)}
          </div>
          <div className="space-y-2">
            <p className={cn("font-medium", textClass)}>{data.title}</p>
            <p className={cn("text-sm", mutedClass)}>{data.description || tLC("reviewNoDescription")}</p>
            <p className={cn("text-sm", textClass)}>{tLC("reviewQuantity")}: {(data as { listing_format?: string }).listing_format === "auction" ? 1 : data.quantity}</p>
            {(data as { listing_format?: string }).listing_format === "auction" ? (
              <p className={cn("text-sm font-medium", textClass)}>{tLC("reviewAuction")}: {tLC("reviewStarting")} {data.auction_start_price != null ? formatPrice(data.auction_start_price, data.currency || "USD") : "—"}{data.auction_reserve_price != null ? `, ${tLC("reviewReserve")} ${formatPrice(data.auction_reserve_price, data.currency || "USD")}` : ""}</p>
            ) : (
              <p className={cn("text-sm font-medium", textClass)}>{tLC("price")}: {data.price ? formatPrice(data.price, data.currency || "USD") : tLC("reviewNotSpecified")}</p>
            )}
          </div>
        </motion.div>

        {(data as { listing_format?: string }).listing_format === "auction" && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            className={cn(cardRadius, cardBase, cardPadding)}
          >
            <div className="flex items-center mb-3">
              <p className={cn(sectionLabel)}>{tLC("reviewAuctionSettings")}</p>
              {editBtn(4)}
            </div>
            <div className="space-y-2">
              <p className={cn("text-sm", textClass)}>{tLC("reviewAuctionType")}: {tLC("reviewTimedAuction")}</p>
              <p className={cn("text-sm", textClass)}>{tLC("startingPrice")}: {data.auction_start_price != null ? formatPrice(data.auction_start_price, data.currency || "USD") : "—"}</p>
              {data.auction_reserve_price != null && <p className={cn("text-sm", textClass)}>{tLC("reviewReserve")}: {formatPrice(data.auction_reserve_price, data.currency || "USD")} ({tLC("reserveHidden")})</p>}
              <p className={cn("text-sm", textClass)}>{tLC("ends")} {formatAuctionEndCopy(computeAuctionEndAt(data.scheduled_publish_at ? new Date(data.scheduled_publish_at) : new Date()))}</p>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={cn(cardRadius, cardBase, cardPadding)}
        >
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{tLC("reviewConditionGrade")}</p>
            {editBtn(5)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={cellBase}>
              <p className={cn("text-xs", mutedClass)}>{tLC("condition")}</p>
              <p className={cn("text-sm font-medium", textClass)}>{conditionLabel}</p>
            </div>
            <div className={cellBase}>
              <p className={cn("text-xs", mutedClass)}>{tLC("grading")}</p>
              <p className={cn("text-sm font-medium", textClass)}>{gradeLabel}</p>
            </div>
          </div>
        </motion.div>

        {/* ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — Location section removed.
            Origin is selected/displayed inside ShippingStep via
            ShipFromAddressPicker. */}

        {(data as any).visibility === "chat_private" ? (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className={cn(cardRadius, cardBase, cardPadding)}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-sm font-medium truncate", textClass)}>{tLC("reviewSaveForReuse")}</p>
                <p className={cn("text-xs", mutedClass)}>{tLC("reviewPreviousPrivateListings")}</p>
              </div>
              <div>
                <Switch
                  checked={Boolean((data as any).chat_private_saved)}
                  onCheckedChange={(checked) => {
                    updateDataAction({ chat_private_saved: checked } as any)
                  }}
                />
              </div>
            </div>
          </motion.div>
        ) : null}

        {((data as any).visibility !== "chat_private" && (data.scheduled_publish_at || data.is_dev_listing)) ? (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className={cn(cardRadius, cardBase, cardPadding)}
          >
            <div className="flex items-center mb-3">
              <p className={cn(sectionLabel)}>{tLC("reviewScheduleVisibility")}</p>
              {editBtn(8)}
            </div>
            <div className="space-y-2">
              {data.scheduled_publish_at ? (
                <div className={cellBase}>
                  <p className={cn("text-xs", mutedClass)}>{tLC("reviewScheduledFor")}</p>
                  <p className={cn("text-sm font-medium", textClass)}>
                    {new Date(data.scheduled_publish_at).toLocaleString(locale, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </p>
                  <p className={cn("text-xs mt-1", mutedClass)}>
                    ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </p>
                </div>
              ) : (
                <div className={cellBase}>
                  <p className={cn("text-sm", textClass)}>{tLC("publishImmediatelyHint")}</p>
                </div>
              )}
              {data.is_dev_listing && (
                <div className="rounded-xl bg-orange-500/10 border border-orange-500/20 p-3">
                  <p className="text-xs text-orange-600 dark:text-orange-400">
                    <span className="font-semibold">{tLC("devListingPrefix")}</span> {tLC("developmentListingDesc")}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        ) : null}

        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={cn(cardRadius, cardBase, cardPadding)}
        >
          <div className="flex items-center mb-3">
            <p className={cn(sectionLabel)}>{tLC("shippingOptions")}</p>
            {editBtn(7)}
          </div>
          <div className={cellBase}>
            {data.shipping?.methods && data.shipping.methods.length > 0 ? (
              <ul className="space-y-1.5">
                {data.shipping.methods.map((optionId: string) => {
                  const customOption = data.shipping.customOptions.find((opt: any) => opt.id === optionId)
                  return (
                    <li key={optionId} className="flex items-center gap-2">
                      <Check className="w-4 h-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        {customOption ? (
                          <span className={cn("text-sm", textClass)}>
                            {customOption.label} – {formatPrice(customOption.price || 0, data.currency || "USD")}
                            {customOption.allowCombined && (
                              <span className={cn("text-xs ml-2", mutedClass)}>
                                + {tLC("reviewCombined")}: {formatPrice(customOption.combinedFee || 0, data.currency || "USD")}/{tLC("reviewUnit")}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className={cn("text-sm", textClass)}>{defaultShippingMap[optionId]?.label || optionId}</span>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className={cn("text-sm", mutedClass)}>{tLC("reviewNoShipping")}</p>
            )}
          </div>
        </motion.div>
      </motion.div>


      {!hideButton && (
        <div className="pt-6">
          <Button
            onClick={handleCreateListing}
            className={cn(creationFlowPrimaryCtaTallClass, "group")}
            size="lg"
            disabled={isCreating}
          >
            {isCreating ? tLC("creating") : tLC("create")}
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}

    </div>
  )
}